import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { AllegatoImg, openAllegato } from "@/lib/allegati";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, FileDown, Printer, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { TicketsBoard } from "@/pages/TicketsBoard";
import { RegistroModulo } from "@/components/registroEffettivo/RegistroModulo";
import { ConfrontoComputo } from "@/components/registroEffettivo/ConfrontoComputo";
import { SalE5Tab } from "@/components/salE5/SalE5Tab";

const isExcel = (p) => p.computoSource === "xlsx_analisi" && p.computoId;

// Computi Excel: registro unico (consegnato/utilizzato/giacenza + noli); gli ordini esistenti restano l'ORDINATO
export function OrdiniTab(props) {
  if (!isExcel(props)) return <OrdiniLegacyTab {...props} />;
  return (
    <div className="space-y-4">
      <RegistroModulo cantiere={props.cantiere} computoId={props.computoId} tipi={["MATERIALE", "NOLO"]} titolo="Materiali e noli effettivi" />
      <OrdiniLegacyTab {...props} />
    </div>
  );
}

export function ManodoperaEffettivaTab(props) {
  if (!isExcel(props)) return <ManodoperaLegacyTab {...props} />;
  return <RegistroModulo cantiere={props.cantiere} computoId={props.computoId} tipi={["MANODOPERA"]} titolo="Manodopera effettiva (a ore e a misura)" />;
}

export function CostiTab(props) {
  if (!isExcel(props)) return <CostiLegacyTab {...props} />;
  return <ConfrontoComputo computoId={props.computoId} />;
}

// SAL E5 solo per i computi Excel; i SAL storici restano nella sezione Subappaltatori
export function SalTab(props) {
  if (isExcel(props)) return <SalE5Tab computoId={props.computoId} />;
  return (
    <Card><CardContent className="p-6 text-sm text-slate-600" data-testid="sal-non-excel">
      I SAL generale e subappaltatori di questa sezione sono disponibili per i computi Excel con analisi: seleziona un computo Excel nel tab <b>Computo</b>. I SAL già emessi dei computi storici restano consultabili nella sezione <b>Subappaltatori</b>.
    </CardContent></Card>
  );
}

const STATI = ["da_ordinare","ordinato","confermato","in_consegna","consegnato","in_ritardo","annullato"];
const STATO_CLR = { consegnato: "bg-emerald-500/20 text-emerald-700", da_ordinare: "bg-slate-500/20", in_ritardo: "bg-rose-500/20 text-rose-700", annullato: "bg-slate-300 text-slate-600" };

/**
 * Banner mostrato in cima ai tab per-cantiere quando esistono >1 computi e uno
 * è selezionato: indica esplicitamente che i dati sono filtrati sul computo
 * scelto. Se computiCount<=1 non appare (mantiene UX identica al pre).
 */
function ComputoScopeBanner({ computoNome, computiCount }) {
  if (!computoNome || (computiCount || 0) <= 1) return null;
  return (
    <div className="rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-300/60 px-3 py-2 text-xs text-amber-800 dark:text-amber-200 flex items-center gap-2" data-testid="computo-scope-banner">
      <span className="text-amber-600">●</span>
      Dati filtrati sul computo <b>{computoNome}</b>. Cambia computo dalla barra <b>Computi del cantiere</b> nel tab Computo.
    </div>
  );
}

function ExportButtons({ cantiereId }) {
  const download = async (fmt) => {
    try {
      const res = await api.get(`/export/ordini.${fmt}`, {
        params: { cantiere_id: cantiereId }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `ordini_${cantiereId.slice(0,8)}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Errore export"); }
  };
  return (
    <div className="flex gap-1">
      <Button size="sm" variant="outline" onClick={() => download("csv")} data-testid="export-ord-csv" title="Export CSV"><FileDown className="h-3.5 w-3.5 mr-1" /> CSV</Button>
      <Button size="sm" variant="outline" onClick={() => download("xlsx")} data-testid="export-ord-xlsx" title="Export XLSX"><FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> XLSX</Button>
      <Button size="sm" variant="outline" onClick={() => download("pdf")} data-testid="export-ord-pdf" title="Export PDF"><FileText className="h-3.5 w-3.5 mr-1" /> PDF</Button>
      <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="export-ord-print" title="Stampa"><Printer className="h-3.5 w-3.5 mr-1" /> Stampa</Button>
    </div>
  );
}

function OrdiniLegacyTab({ cantiere, computoId, computoNome, computiCount }) {
  const { user } = useAuth();
  const canEdit = user && (user.role === "ADMIN" || user.role === "TECNICO");
  const [ordini, setOrdini] = useState([]);
  const [fabbisogno, setFabbisogno] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ descrizione_materiale: "", unita_misura: "", quantita: "", prezzo_unitario: "", stato: "da_ordinare", data_ordine: "", data_prevista_consegna: "", numero_ordine: "" });
  const load = async () => {
    const params = computoId ? { computo_id: computoId } : {};
    const [o, f] = await Promise.all([
      api.get(`/cantieri/${cantiere.id}/ordini`, { params }),
      api.get(`/cantieri/${cantiere.id}/fabbisogno-materiali`, { params }),
    ]);
    setOrdini(o.data); setFabbisogno(f.data);
  };
  useEffect(() => { load(); }, [cantiere.id, computoId]);
  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    ["quantita","prezzo_unitario"].forEach(k => { if (payload[k] === "") payload[k] = null; else payload[k] = Number(payload[k]); });
    ["data_ordine","data_prevista_consegna"].forEach(k => { if (payload[k] === "") payload[k] = null; });
    try { await api.post(`/cantieri/${cantiere.id}/ordini`, payload); toast.success("Ordine creato"); setOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const patchStato = async (id, stato) => { try { await api.patch(`/ordini/${id}`, { stato }); load(); } catch (e) { toast.error("Errore"); } };
  const del = async (id) => { if (!confirm("Eliminare?")) return; try { await api.delete(`/ordini/${id}`); load(); } catch (e) { toast.error("Errore"); } };
  const fmt = (n) => n == null ? "—" : Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div className="space-y-6">
      <ComputoScopeBanner computoNome={computoNome} computiCount={computiCount} />
      <Card><CardContent className="p-6">
        <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold mb-3">Fabbisogno vs Ordini</div>
        {fabbisogno.length === 0 ? <p className="text-slate-500 text-sm">Nessun fabbisogno calcolato. Aggiungi materiali previsti alle righe del computo.</p> : (
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow><TableHead>Materiale</TableHead><TableHead>UM</TableHead><TableHead className="text-right">Necessari</TableHead><TableHead className="text-right">Ordinati</TableHead><TableHead className="text-right">Da ordinare</TableHead><TableHead className="text-right">Consegnati</TableHead><TableHead className="text-right">Da consegnare</TableHead></TableRow></TableHeader>
            <TableBody>{fabbisogno.map((f, i) => (<TableRow key={`${f.descrizione}::${f.unita_misura || ""}::${i}`}><TableCell className="font-medium">{f.descrizione}</TableCell><TableCell>{f.unita_misura || "—"}</TableCell><TableCell className="text-right font-mono">{fmt(f.necessari)}</TableCell><TableCell className="text-right font-mono">{fmt(f.ordinati)}</TableCell><TableCell className="text-right font-mono font-semibold text-amber-700">{fmt(f.da_ordinare)}</TableCell><TableCell className="text-right font-mono">{fmt(f.consegnati)}</TableCell><TableCell className="text-right font-mono">{fmt(f.da_consegnare)}</TableCell></TableRow>))}</TableBody>
          </Table></div>
        )}
      </CardContent></Card>
      <Card><CardContent className="p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold">Ordini materiali</div>
          <div className="flex gap-2 flex-wrap">
            <ExportButtons cantiereId={cantiere.id} />
            {canEdit && <Button size="sm" onClick={() => setOpen(true)} data-testid="new-ordine-btn" className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"><Plus className="h-3.5 w-3.5 mr-1" /> Nuovo ordine</Button>}
          </div>
        </div>
        <div className="overflow-x-auto"><Table>
          <TableHeader><TableRow><TableHead>N. ordine</TableHead><TableHead>Capitolo</TableHead><TableHead>N.Ord</TableHead><TableHead>Voce</TableHead><TableHead>Materiale</TableHead><TableHead>Q.tà</TableHead><TableHead>Fornitore</TableHead><TableHead>Consegna</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Costo</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>{ordini.length === 0 ? <TableRow><TableCell colSpan={11} className="text-center py-6 text-slate-500">Nessun ordine</TableCell></TableRow> : ordini.map(o => (
            <TableRow key={o.id} data-testid={`ordine-row-${o.id}`}>
              <TableCell className="font-mono text-xs">{o.numero_ordine || "—"}</TableCell>
              <TableCell className="text-xs">{o.capitolo_nome || "—"}</TableCell>
              <TableCell className="font-mono text-xs">{o.riga_num_ord || "—"}</TableCell>
              <TableCell className="text-xs max-w-[200px] truncate" title={o.riga_designazione || ""}>{o.riga_designazione || "—"}</TableCell>
              <TableCell className="font-medium">{o.descrizione_materiale}</TableCell>
              <TableCell className="font-mono">{fmt(o.quantita)} {o.unita_misura || ""}</TableCell>
              <TableCell className="text-xs">{o.fornitore_nome || "—"}</TableCell>
              <TableCell className="text-xs">{o.data_prevista_consegna || "—"}</TableCell>
              <TableCell>{canEdit ? (
                <Select value={o.stato} onValueChange={(v) => patchStato(o.id, v)}>
                  <SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATI.map(s => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}</SelectContent>
                </Select>
              ) : <Badge variant="outline" className="capitalize">{o.stato}</Badge>}</TableCell>
              <TableCell className="text-right font-mono">€ {fmt(o.costo_totale)}</TableCell>
              <TableCell>{canEdit && <Button size="icon" variant="ghost" onClick={() => del(o.id)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>}</TableCell>
            </TableRow>
          ))}</TableBody>
        </Table></div>
      </CardContent></Card>
      <Dialog open={open} onOpenChange={setOpen}><DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Nuovo ordine materiale</DialogTitle><DialogDescription>Il calendario materiali si aggiornerà automaticamente.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Materiale *</Label><Input required value={form.descrizione_materiale} onChange={(e) => setForm({ ...form, descrizione_materiale: e.target.value })} data-testid="ord-desc" /></div>
          <div><Label>UM</Label><Input value={form.unita_misura} onChange={(e) => setForm({ ...form, unita_misura: e.target.value })} /></div>
          <div><Label>Quantità *</Label><Input type="number" step="0.01" required value={form.quantita} onChange={(e) => setForm({ ...form, quantita: e.target.value })} data-testid="ord-qta" /></div>
          <div><Label>Prezzo unit.</Label><Input type="number" step="0.01" value={form.prezzo_unitario} onChange={(e) => setForm({ ...form, prezzo_unitario: e.target.value })} data-testid="ord-prezzo" /></div>
          <div><Label>N. ordine</Label><Input value={form.numero_ordine} onChange={(e) => setForm({ ...form, numero_ordine: e.target.value })} /></div>
          <div><Label>Data ordine</Label><Input type="date" value={form.data_ordine} onChange={(e) => setForm({ ...form, data_ordine: e.target.value })} /></div>
          <div><Label>Data prevista consegna</Label><Input type="date" value={form.data_prevista_consegna} onChange={(e) => setForm({ ...form, data_prevista_consegna: e.target.value })} data-testid="ord-consegna" /></div>
          <div className="col-span-2"><Label>Stato</Label>
            <Select value={form.stato} onValueChange={(v) => setForm({ ...form, stato: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{STATI.map(s => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter className="col-span-2"><Button type="submit" data-testid="ord-submit" className="bg-amber-500 text-slate-950 font-semibold">Crea</Button></DialogFooter>
        </form>
      </DialogContent></Dialog>
    </div>
  );
}

function ManodoperaLegacyTab({ cantiere, computoId, computoNome, computiCount }) {
  const { user } = useAuth();
  const canEdit = user && (user.role === "ADMIN" || user.role === "TECNICO");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [subs, setSubs] = useState([]);
  const [dips, setDips] = useState([]);
  const [saving, setSaving] = useState(false);
  const _uidTM = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `k_${Date.now()}_${Math.random().toString(36).slice(2)}`);
  const emptyLine = () => ({ _key: _uidTM(), dipendente_id: "", tipo_operaio: "", ore: "8", costo_orario: "35" });
  const emptyHeader = { subappaltatore_anag_id: "none", data: new Date().toISOString().slice(0,10), squadra: "", note: "" };
  const [header, setHeader] = useState(emptyHeader);
  const [lines, setLines] = useState([emptyLine()]);
  const load = async () => {
    const params = computoId ? { computo_id: computoId } : {};
    const { data } = await api.get(`/cantieri/${cantiere.id}/manodopera-effettiva`, { params });
    setRows(data);
  };
  useEffect(() => { load(); }, [cantiere.id, computoId]);
  useEffect(() => { api.get("/anagrafiche/subappaltatori").then(r => setSubs(r.data || [])).catch(() => setSubs([])); }, []);
  useEffect(() => {
    if (header.subappaltatore_anag_id && header.subappaltatore_anag_id !== "none") {
      api.get("/dipendenti-subappaltatori", { params: { subappaltatore_id: header.subappaltatore_anag_id } })
        .then(r => setDips(r.data || [])).catch(() => setDips([]));
    } else { setDips([]); }
    setLines([emptyLine()]);
  }, [header.subappaltatore_anag_id]);

  const openDlg = () => { setHeader({ ...emptyHeader, data: new Date().toISOString().slice(0,10) }); setLines([emptyLine()]); setOpen(true); };
  const addLine = () => setLines([...lines, emptyLine()]);
  const removeLine = (i) => setLines(lines.length > 1 ? lines.filter((_, idx) => idx !== i) : lines);
  const updateLine = (i, patch) => setLines(lines.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const onPickDip = (i, v) => {
    const dip = dips.find(d => d.id === v);
    updateLine(i, { dipendente_id: v, tipo_operaio: dip?.ruolo || lines[i].tipo_operaio || "" });
  };
  const usedDipIds = new Set(lines.map(l => l.dipendente_id).filter(Boolean));

  const submit = async (e) => {
    e.preventDefault();
    if (header.subappaltatore_anag_id === "none") { toast.error("Seleziona un subappaltatore"); return; }
    const valid = lines.filter(l => l.dipendente_id && Number(l.ore) > 0 && Number(l.costo_orario) > 0);
    if (valid.length === 0) { toast.error("Aggiungi almeno un dipendente con ore ed €/h"); return; }
    if (new Set(valid.map(l => l.dipendente_id)).size !== valid.length) { toast.error("Dipendente ripetuto: usa una sola riga per persona"); return; }
    setSaving(true);
    try {
      for (const l of valid) {
        const dip = dips.find(d => d.id === l.dipendente_id);
        await api.post(`/cantieri/${cantiere.id}/manodopera-effettiva`, {
          operaio_nome: dip ? `${dip.cognome} ${dip.nome}`.trim() : "—",
          squadra: header.squadra || null,
          data: header.data,
          ore: Number(l.ore),
          costo_orario: Number(l.costo_orario),
          note: header.note || null,
          tipo_operaio: l.tipo_operaio || null,
          num_operai: 1,
          dipendente_id: l.dipendente_id,
        });
      }
      toast.success(`${valid.length} registrazion${valid.length === 1 ? "e creata" : "i create"}`);
      setOpen(false); setHeader(emptyHeader); setLines([emptyLine()]); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const del = async (id) => { if (!confirm("Eliminare?")) return; try { await api.delete(`/manodopera-effettiva/${id}`); load(); } catch { toast.error("Errore"); } };
  const fmt = (n) => n == null ? "—" : Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const tot = rows.reduce((s, r) => s + Number(r.costo_totale || 0), 0);
  const linesTot = lines.reduce((s, l) => s + (Number(l.ore || 0) * Number(l.costo_orario || 0)), 0);

  return (
    <>
      <ComputoScopeBanner computoNome={computoNome} computiCount={computiCount} />
      <Card><CardContent className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold">Manodopera effettiva giornaliera</div>
        {canEdit && <Button size="sm" onClick={openDlg} data-testid="new-man-eff" className="bg-amber-500 text-slate-950 font-semibold"><Plus className="h-3.5 w-3.5 mr-1" /> Registra</Button>}
      </div>
      <div className="overflow-x-auto"><Table>
        <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Operaio / Dipendente</TableHead><TableHead>Subappaltatore</TableHead><TableHead>Tipo operaio</TableHead><TableHead className="text-right">Ore</TableHead><TableHead className="text-right">€/h</TableHead><TableHead className="text-right">Costo</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>{rows.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-6 text-slate-500">Nessuna registrazione</TableCell></TableRow> : rows.map(r => (
          <TableRow key={r.id} data-testid={`man-eff-row-${r.id}`}>
            <TableCell className="font-mono text-xs">{r.data}</TableCell>
            <TableCell>
              <b>{r.dipendente_nome || r.operaio_nome}</b>
              {r.dipendente_is_archived && <Badge className="ml-1 bg-slate-500 text-white text-[9px]">archiviato</Badge>}
              {r.squadra && <span className="text-xs text-slate-500"> · {r.squadra}</span>}
            </TableCell>
            <TableCell className="text-xs">{r.dipendente_sub_nome || "—"}</TableCell>
            <TableCell className="text-xs">{r.tipo_operaio || "—"}</TableCell>
            <TableCell className="text-right font-mono">{fmt(r.ore)}</TableCell>
            <TableCell className="text-right font-mono">{fmt(r.costo_orario)}</TableCell>
            <TableCell className="text-right font-mono font-semibold">€ {fmt(r.costo_totale)}</TableCell>
            <TableCell>{canEdit && <Button size="icon" variant="ghost" onClick={() => del(r.id)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>}</TableCell>
          </TableRow>
        ))}{rows.length > 0 && <TableRow><TableCell colSpan={6} className="text-right font-bold">Totale</TableCell><TableCell className="text-right font-mono font-bold">€ {fmt(tot)}</TableCell><TableCell></TableCell></TableRow>}</TableBody>
      </Table></div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Registrazione giornaliera manodopera</DialogTitle>
            <DialogDescription>Seleziona il subappaltatore, poi aggiungi una riga per ogni dipendente con ore ed €/h. Salvataggio: 1 record per dipendente.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2"><Label>Subappaltatore *</Label>
                <Select value={header.subappaltatore_anag_id} onValueChange={(v) => setHeader({ ...header, subappaltatore_anag_id: v })}>
                  <SelectTrigger data-testid="me-sub-select"><SelectValue placeholder="Seleziona subappaltatore" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— nessuno —</SelectItem>{subs.map(s => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Data *</Label><Input type="date" required value={header.data} onChange={(e) => setHeader({ ...header, data: e.target.value })} data-testid="me-data" /></div>
              <div className="md:col-span-3"><Label>Squadra / Note</Label><Input value={header.squadra} onChange={(e) => setHeader({ ...header, squadra: e.target.value })} placeholder="Opzionale" data-testid="me-squadra" /></div>
            </div>

            <div className="border rounded-md p-3 bg-slate-50 dark:bg-slate-900/40">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold">Dipendenti impiegati</div>
                <Button type="button" size="sm" variant="outline" onClick={addLine} disabled={header.subappaltatore_anag_id === "none"} data-testid="me-add-line"><Plus className="h-3 w-3 mr-1" /> Aggiungi dipendente</Button>
              </div>
              {header.subappaltatore_anag_id === "none" ? (
                <p className="text-xs text-slate-500 text-center py-3">Seleziona prima un subappaltatore.</p>
              ) : dips.length === 0 ? (
                <p className="text-xs text-slate-500 text-center py-3">Nessun dipendente attivo per questo subappaltatore. Aggiungili da &quot;Anagrafica subappaltatori&quot;.</p>
              ) : (
                <div className="space-y-2">
                  {lines.map((l, i) => {
                    const rowTot = Number(l.ore || 0) * Number(l.costo_orario || 0);
                    return (
                      <div key={l._key || i} className="grid grid-cols-12 gap-2 items-end" data-testid={`me-line-${i}`}>
                        <div className="col-span-4"><Label className="text-[10px]">Dipendente *</Label>
                          <Select value={l.dipendente_id} onValueChange={(v) => onPickDip(i, v)}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`me-line-dip-${i}`}><SelectValue placeholder="Scegli…" /></SelectTrigger>
                            <SelectContent>{dips.filter(d => d.id === l.dipendente_id || !usedDipIds.has(d.id)).map(d => (
                              <SelectItem key={d.id} value={d.id}>{d.cognome} {d.nome}{d.ruolo ? ` · ${d.ruolo}` : ""}</SelectItem>
                            ))}</SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-3"><Label className="text-[10px]">Tipo operaio</Label>
                          <Input className="h-8 text-xs" value={l.tipo_operaio} onChange={(e) => updateLine(i, { tipo_operaio: e.target.value })} placeholder="Es. Muratore" data-testid={`me-line-tipo-${i}`} />
                        </div>
                        <div className="col-span-2"><Label className="text-[10px]">Ore *</Label>
                          <Input className="h-8 text-xs" type="number" step="0.5" min="0" value={l.ore} onChange={(e) => updateLine(i, { ore: e.target.value })} data-testid={`me-line-ore-${i}`} />
                        </div>
                        <div className="col-span-2"><Label className="text-[10px]">€/h *</Label>
                          <Input className="h-8 text-xs" type="number" step="0.01" min="0" value={l.costo_orario} onChange={(e) => updateLine(i, { costo_orario: e.target.value })} data-testid={`me-line-costo-${i}`} />
                        </div>
                        <div className="col-span-1 flex items-center justify-end">
                          <Button type="button" size="icon" variant="ghost" onClick={() => removeLine(i)} disabled={lines.length === 1} data-testid={`me-line-del-${i}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                        </div>
                        <div className="col-span-12 text-right text-[10px] font-mono text-slate-500 -mt-1">Totale riga: € {fmt(rowTot || 0)}</div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="mt-3 pt-2 border-t text-right text-sm font-semibold">Totale complessivo: <span className="text-amber-600 font-mono">€ {fmt(linesTot || 0)}</span></div>
            </div>

            <DialogFooter><Button type="submit" disabled={saving} data-testid="me-submit" className="bg-amber-500 text-slate-950 font-semibold">{saving ? "Salvataggio…" : "Registra"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </CardContent></Card>
    </>
  );
}

function CostiLegacyTab({ cantiere, computoId, computoNome, computiCount }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const params = computoId ? { computo_id: computoId } : {};
    api.get(`/cantieri/${cantiere.id}/costi/riepilogo`, { params }).then(r => setData(r.data));
  }, [cantiere.id, computoId]);
  if (!data) return <div className="text-slate-500 p-4">Caricamento…</div>;
  const t = data.totale_cantiere;
  const fmt = (n) => Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const marg = (v) => v >= 0 ? "emerald" : "rose";
  return (
    <div className="space-y-6">
      <ComputoScopeBanner computoNome={computoNome} computiCount={computiCount} />
      <Card><CardContent className="p-6 space-y-6">
        <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold">Riepilogo economico totale cantiere</div>

        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">A) Costi previsti</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatBox label="Ricavo (importo computo)" val={t.ricavo} />
            <StatBox label="Materiali previsti" val={t.prev_materiali} />
            <StatBox label="Manodopera prevista" val={t.prev_manodopera} />
            <StatBox label="Margine previsto" val={t.margine_previsto} highlight={marg(t.margine_previsto)} testid="cc-tot-margine-prev" />
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-blue-600 font-semibold mb-2">B) Costi effettivi — Manodopera a ORE</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatBox label="Ordini materiali (eff.)" val={t.eff_materiali} />
            <StatBox label="Manodopera a ore" val={t.eff_manodopera_ore} />
            <StatBox label="Totale effettivo ore" val={t.eff_totale_ore} />
            <StatBox label="Margine effettivo ore" val={t.margine_effettivo_ore} highlight={marg(t.margine_effettivo_ore)} testid="cc-tot-margine-eff-ore" />
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-emerald-600 font-semibold mb-2">C) Costi effettivi — Manodopera a QUANTITÀ</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatBox label="Ordini materiali (eff.)" val={t.eff_materiali} />
            <StatBox label="Manodopera a quantità" val={t.eff_manodopera_qta} />
            <StatBox label="Totale effettivo quantità" val={t.eff_totale_qta} />
            <StatBox label="Margine effettivo quantità" val={t.margine_effettivo_qta} highlight={marg(t.margine_effettivo_qta)} testid="cc-tot-margine-eff-qta" />
          </div>
        </div>
        <div className="text-[10px] text-slate-500 italic">
          Materiali effettivi = somma ordini materiali collegati alle righe (non annullati). MdO ORE = ManodoperaEffettiva.costo_totale. MdO QUANTITÀ = Σ(rilievo × prezzo contrattuale subappaltatore).
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-6">
        <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold mb-3">Per capitolo</div>
        <div className="overflow-x-auto"><Table>
          <TableHeader><TableRow>
            <TableHead>Capitolo</TableHead>
            <TableHead className="text-right">Ricavo</TableHead>
            <TableHead className="text-right">Prev.</TableHead>
            <TableHead className="text-right">Eff. ore</TableHead>
            <TableHead className="text-right">Eff. quantità</TableHead>
            <TableHead className="text-right">Marg. previsto</TableHead>
            <TableHead className="text-right">Marg. eff. ore</TableHead>
            <TableHead className="text-right">Marg. eff. quantità</TableHead>
          </TableRow></TableHeader>
          <TableBody>{data.per_capitolo.map(c => (
            <TableRow key={c.capitolo_id}>
              <TableCell className="font-medium">{c.nome}</TableCell>
              <TableCell className="text-right font-mono">€ {fmt(c.ricavo)}</TableCell>
              <TableCell className="text-right font-mono">€ {fmt(c.prev_totale)}</TableCell>
              <TableCell className="text-right font-mono">€ {fmt(c.eff_totale_ore)}</TableCell>
              <TableCell className="text-right font-mono">€ {fmt(c.eff_totale_qta)}</TableCell>
              <TableCell className="text-right font-mono">€ {fmt(c.margine_previsto)}</TableCell>
              <TableCell className={`text-right font-mono font-semibold ${c.margine_effettivo_ore >= 0 ? "text-emerald-600" : "text-rose-600"}`}>€ {fmt(c.margine_effettivo_ore)}</TableCell>
              <TableCell className={`text-right font-mono font-semibold ${c.margine_effettivo_qta >= 0 ? "text-emerald-600" : "text-rose-600"}`}>€ {fmt(c.margine_effettivo_qta)}</TableCell>
            </TableRow>
          ))}</TableBody>
        </Table></div>
      </CardContent></Card>
    </div>
  );
}

function StatBox({ label, val, sub, highlight }) {
  const fmt = (n) => Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const cls = highlight === "emerald" ? "text-emerald-600" : highlight === "rose" ? "text-rose-600" : "text-slate-900 dark:text-slate-100";
  return <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4"><div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div><div className={`text-xl font-extrabold mt-1 font-mono ${cls}`}>€ {fmt(val)}</div>{sub && <div className="text-xs text-slate-500 mt-1 font-mono">{sub}</div>}</div>;
}

export function AllegatiTab({ cantiere, tipo, computoId, computoNome, computiCount }) {
  const { user } = useAuth();
  const canEdit = user && (user.role === "ADMIN" || user.role === "TECNICO");
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const load = async () => {
    const params = { cantiere_id: cantiere.id, tipo };
    if (computoId) params.computo_id = computoId;
    const { data } = await api.get(`/allegati`, { params });
    setItems(data);
  };
  useEffect(() => { load(); }, [cantiere.id, tipo, computoId]);
  const upload = async (e) => {
    const files = e.target.files; if (!files?.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file); fd.append("tipo", tipo); fd.append("link_type", "cantiere"); fd.append("cantiere_id", cantiere.id);
      try { await api.post("/allegati/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); } catch (e) { toast.error(e.response?.data?.detail || "Errore upload"); }
    }
    setUploading(false); toast.success("Upload completato"); load(); e.target.value = "";
  };
  const del = async (id) => { if (!confirm("Eliminare?")) return; try { await api.delete(`/allegati/${id}`); load(); } catch { toast.error("Errore"); } };
  const groupedFoto = tipo === "foto" ? items.reduce((acc, a) => {
    const key = a.capitolo_id
      ? `${a.capitolo_nome || "Capitolo"}${a.riga_num_ord ? ` · N.Ord ${a.riga_num_ord}` : ""}`
      : "Senza collegamento a computo";
    (acc[key] = acc[key] || []).push(a);
    return acc;
  }, {}) : null;
  return (
    <div className="space-y-3">
      <ComputoScopeBanner computoNome={computoNome} computiCount={computiCount} />
      <Card><CardContent className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold">{tipo === "foto" ? "Fotografie" : "Documenti"}</div>
        {canEdit && <label className="cursor-pointer"><input type="file" multiple {...(tipo === "foto" ? { accept: "image/*", capture: "environment" } : {})} className="hidden" onChange={upload} data-testid={`upload-${tipo}`} /><span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold text-sm"><Plus className="h-3.5 w-3.5" /> {uploading ? "Upload…" : tipo === "foto" ? "+ Foto" : "+ Documento"}</span></label>}
      </div>
      {items.length === 0 ? <p className="text-center text-slate-500 py-6">Nessun {tipo}</p> : tipo === "foto" ? (
        <div className="space-y-6">{Object.keys(groupedFoto).sort().map(key => (
          <div key={key} data-testid={`foto-group-${key}`}>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2 pb-1 border-b border-slate-200 dark:border-slate-800">{key} <span className="text-slate-400">({groupedFoto[key].length})</span></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">{groupedFoto[key].map(a => (
              <div key={a.id} className="relative group aspect-square rounded-lg overflow-hidden border border-slate-200 dark:border-slate-800 cursor-zoom-in" onClick={() => openAllegato(a.id, a.filename)}>
                <AllegatoImg id={a.id} className="w-full h-full object-cover" alt={a.filename} />
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] p-1 truncate">{a.created_at?.slice(0,10)}</div>
                {canEdit && <button onClick={(e) => { e.stopPropagation(); del(a.id); }} className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 bg-rose-600 text-white rounded p-1"><Trash2 className="h-3 w-3" /></button>}
              </div>
            ))}</div>
          </div>
        ))}</div>
      ) : (
        <Table><TableBody>{items.map(a => (
          <TableRow key={a.id}>
            <TableCell className="font-medium"><button type="button" onClick={() => openAllegato(a.id, a.filename)} className="text-blue-600 hover:underline text-left" data-testid={`doc-open-${a.id}`}>{a.filename}</button></TableCell>
            <TableCell className="text-xs text-slate-500">{Math.round((a.size_bytes || 0) / 1024)} KB</TableCell>
            <TableCell className="text-right">{canEdit && <Button size="icon" variant="ghost" onClick={() => del(a.id)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>}</TableCell>
          </TableRow>
        ))}</TableBody></Table>
      )}
    </CardContent></Card>
    </div>
  );
}

export function NoteTab({ cantiere, computoId, computoNome, computiCount }) {
  const { user } = useAuth();
  const canEdit = user && ["ADMIN","TECNICO"].includes(user.role);
  const [notes, setNotes] = useState([]);
  const [testo, setTesto] = useState("");
  const [filter, setFilter] = useState("all");
  const load = async () => {
    const params = { cantiere_id: cantiere.id };
    if (computoId) params.computo_id = computoId;
    const { data } = await api.get(`/note`, { params });
    setNotes(data);
  };
  useEffect(() => { load(); }, [cantiere.id, computoId]);
  const create = async () => { if (!testo.trim()) return; try { await api.post("/note", { cantiere_id: cantiere.id, testo }); setTesto(""); toast.success("Nota aggiunta"); load(); } catch(e) { toast.error(e.response?.data?.detail || "Errore"); } };
  const del = async (id) => { if (!confirm("Eliminare?")) return; try { await api.delete(`/note/${id}`); load(); } catch(e) { toast.error(e.response?.data?.detail || "Errore"); } };
  const filtered = notes.filter(n => {
    if (filter === "all") return true;
    if (filter === "riga") return !!n.riga_computo_id;
    if (filter === "capitolo") return !!n.capitolo_id && !n.riga_computo_id;
    if (filter === "cantiere") return !n.capitolo_id && !n.riga_computo_id;
    return true;
  });
  return (
    <div className="space-y-4">
      <ComputoScopeBanner computoNome={computoNome} computiCount={computiCount} />
      {canEdit && <Card><CardContent className="p-6">
        <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold mb-3">Nuova nota di cantiere</div>
        <div className="flex flex-col gap-2">
          <textarea placeholder="Scrivi la tua nota…" value={testo} onChange={(e) => setTesto(e.target.value)} rows={3} className="px-3 py-2 border border-slate-200 dark:border-slate-800 rounded-md bg-white dark:bg-slate-900 text-sm resize-none" data-testid="nota-testo-input" />
          <div className="flex justify-between items-center">
            <span className="text-xs text-slate-500">Autore: <b>{user?.full_name || user?.email}</b></span>
            <Button size="sm" onClick={create} disabled={!testo.trim()} data-testid="nota-submit" className="bg-amber-500 text-slate-950 font-semibold">Aggiungi nota</Button>
          </div>
        </div>
      </CardContent></Card>}
      <Card><CardContent className="p-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold">Storico note ({filtered.length})</div>
          <div className="flex gap-1">
            {[["all","Tutte"],["cantiere","Cantiere"],["capitolo","Capitolo"],["riga","Voce"]].map(([k,l]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`nota-filter-${k}`} className={`px-2 py-1 text-xs rounded ${filter === k ? "bg-amber-500 text-slate-950 font-semibold" : "bg-slate-100 dark:bg-slate-800"}`}>{l}</button>
            ))}
          </div>
        </div>
        {filtered.length === 0 ? <p className="text-center text-slate-500 py-4 text-sm">Nessuna nota</p> : (
          <div className="space-y-3">{filtered.map(n => (
            <div key={n.id} className="border-l-4 border-amber-400 bg-amber-50 dark:bg-amber-950/20 rounded-r-md p-3" data-testid={`nota-${n.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex flex-wrap gap-1 mb-1">
                    {n.riga_computo_id ? <Badge variant="outline" className="text-[10px]">{n.capitolo_nome} · N.Ord {n.riga_num_ord || "—"}</Badge>
                      : n.capitolo_nome ? <Badge variant="outline" className="text-[10px]">{n.capitolo_nome}</Badge>
                      : <Badge variant="outline" className="text-[10px]">Cantiere</Badge>}
                  </div>
                  <div className="text-sm whitespace-pre-wrap">{n.testo}</div>
                  <div className="text-[10px] text-slate-500 mt-1"><b>{n.author_name}</b> · {new Date(n.created_at).toLocaleString("it-IT")}</div>
                </div>
                {(user?.role === "ADMIN" || user?.full_name === n.author_name) && (
                  <Button size="icon" variant="ghost" onClick={() => del(n.id)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                )}
              </div>
            </div>
          ))}</div>
        )}
      </CardContent></Card>
    </div>
  );
}


export function TicketsTab({ cantiere, tipo, computoId, computoNome, computiCount }) {
  // Riusa la board post-it della pagina principale, scoping su questo cantiere.
  // Include automaticamente archiviati per Ticket Subappaltatori (storico).
  return (
    <div className="space-y-3">
      <ComputoScopeBanner computoNome={computoNome} computiCount={computiCount} />
      <TicketsBoard tipo={tipo} cantiereId={cantiere.id} computoId={computoId} />
    </div>
  );
}
